import debounce from 'debounce-promise';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IRedemptionCalculationResponse, TRedemptionType } from '@feathr/blackbox';
import { Button, ButtonValid, Modal, toast } from '@feathr/components';
import { useAccount, useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';

import MediaBalanceForm from './MediaBalanceForm';
import type { IMediaBalanceFormErrors, IProjectMediaBalance } from './RedeemCreditsModal.utils';
import { getValidationErrorArray, validateMediaBalanceForm } from './RedeemCreditsModal.utils';
import RedemptionSuccess from './RedemptionSuccess';
import RedemptionTypeSelect from './RedemptionTypeSelect';

type TModalStep = 'select' | 'media-balance' | 'success';

export interface IRedeemCreditsModalProps {
  onClose: () => void;
  onSuccess?: () => void;
  opened: boolean;
}

interface IFormData {
  creditsToRedeem?: number;
  redemptionType?: TRedemptionType;
  selectedProjectId?: string;
  selectedProjectName?: string;
}

const initialFormData: IFormData = {
  creditsToRedeem: undefined,
  redemptionType: undefined,
  selectedProjectId: undefined,
  selectedProjectName: undefined,
};

function RedeemCreditsModal({
  onClose,
  onSuccess,
  opened,
}: Readonly<IRedeemCreditsModalProps>): JSX.Element {
  const { t } = useTranslation();
  const { Events } = useStore();
  const account = useAccount();
  const [formData, setFormData] = useState<IFormData>(initialFormData);
  const [modalStep, setModalStep] = useState<TModalStep>('select');

  const [availableCredits, setAvailableCredits] = useState<number>(0);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);
  const [calculationResult, setCalculationResult] = useState<IRedemptionCalculationResponse | null>(
    null,
  );
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectBalance, setProjectBalance] = useState<IProjectMediaBalance | undefined>(undefined);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [redemptionCompleted, setRedemptionCompleted] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchCredits(): Promise<void> {
      if (!opened || !account) {
        return;
      }
      setIsLoadingCredits(true);
      try {
        const stats = await account.getGrowthCredits();
        if (!cancelled) {
          setAvailableCredits(stats.credit_balance);
        }
      } catch (error) {
        if (!cancelled) {
          toast(t('Failed to load available credits'), { type: 'error' });
        }
      } finally {
        if (!cancelled) {
          setIsLoadingCredits(false);
        }
      }
    }

    fetchCredits();

    return () => {
      cancelled = true;
    };
  }, [opened, account, t]);

  const debouncedCalculate = useMemo(
    () =>
      debounce(async (projectId: string, credits: number) => {
        if (!account) {
          return;
        }
        setIsCalculating(true);
        try {
          const result = await account.calculateRedemption({
            type: 'media',
            target_id: projectId,
            credits_to_redeem: credits,
          });
          setCalculationResult(result);
        } catch {
          setCalculationResult(null);
          toast(t('Failed to calculate conversion. Please try again.'), { type: 'error' });
        } finally {
          setIsCalculating(false);
        }
      }, DEFAULT_DEBOUNCE_WAIT),
    [account, t],
  );

  useEffect(() => {
    const { creditsToRedeem, selectedProjectId } = formData;
    const hasValidCredits =
      creditsToRedeem !== undefined && creditsToRedeem > 0 && creditsToRedeem <= availableCredits;

    if (hasValidCredits && selectedProjectId) {
      debouncedCalculate(selectedProjectId, creditsToRedeem);
    } else {
      setCalculationResult(null);
    }
  }, [formData.creditsToRedeem, formData.selectedProjectId, availableCredits, debouncedCalculate]);

  const convertedAmount = calculationResult?.actual_cash_value ?? 0;

  const validationErrors: IMediaBalanceFormErrors = useMemo(
    () =>
      validateMediaBalanceForm(
        formData.creditsToRedeem,
        availableCredits,
        formData.selectedProjectId,
        t,
      ),
    [formData.creditsToRedeem, formData.selectedProjectId, availableCredits, t],
  );

  const validationErrorArray = useMemo(
    () => getValidationErrorArray(validationErrors),
    [validationErrors],
  );

  function reset(): void {
    setFormData(initialFormData);
    setModalStep('select');
    setCalculationResult(null);
    setProjectBalance(undefined);
    setRedemptionCompleted(false);
  }

  function handleClose(): void {
    if (redemptionCompleted) {
      onSuccess?.();
    }
    reset();
    onClose();
  }

  function handleTypeSelect(type: TRedemptionType): void {
    setFormData((prev) => ({ ...prev, redemptionType: type }));
    if (type === 'media') {
      setModalStep('media-balance');
    }
  }

  async function handleProjectChange(projectId: string): Promise<void> {
    setIsLoadingProject(true);
    setFormData((prev) => ({
      ...prev,
      selectedProjectId: projectId,
      selectedProjectName: undefined,
    }));

    try {
      const project = Events.get(projectId);
      await project.reload();

      setFormData((prev) => ({
        ...prev,
        selectedProjectName: project.name,
      }));

      const billing = project.get('billing');
      if (billing) {
        setProjectBalance({
          currentBalance: billing.media_credit_balance ?? 0,
          spent: billing.billed_spend ?? 0,
        });
      } else {
        setProjectBalance(undefined);
      }
    } catch {
      toast(t('Failed to load project details'), { type: 'error' });
      setProjectBalance(undefined);
    } finally {
      setIsLoadingProject(false);
    }
  }

  function handleCreditsChange(credits: number | undefined): void {
    setFormData((prev) => ({ ...prev, creditsToRedeem: credits }));
  }

  function handleBack(): void {
    if (modalStep === 'media-balance') {
      setModalStep('select');
      setFormData(initialFormData);
      setCalculationResult(null);
      setProjectBalance(undefined);
    }
  }

  const handleConfirmRedemption = useCallback(async (): Promise<void> => {
    if (!account || !formData.selectedProjectId || !formData.creditsToRedeem) {
      return;
    }
    setIsSubmitting(true);
    try {
      await account.submitRedemption({
        type: 'media',
        target_id: formData.selectedProjectId,
        credits_to_redeem: formData.creditsToRedeem,
      });
      setModalStep('success');
      setRedemptionCompleted(true);
    } catch (error) {
      toast(t('Failed to redeem credits. Please try again.'), { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [account, formData.selectedProjectId, formData.creditsToRedeem, t]);

  const mediaBalanceActions = [
    <Button key={'back'} onClick={handleBack}>
      {t('Back')}
    </Button>,
    <ButtonValid
      disabled={isSubmitting || isCalculating || validationErrorArray.length > 0}
      errors={validationErrorArray}
      key={'confirm'}
      onClick={handleConfirmRedemption}
      type={'primary'}
    >
      {isSubmitting ? t('Processing...') : t('Confirm Redemption +')}
    </ButtonValid>,
  ];

  const stepMap = {
    select: {
      title: t('Redeem Growth Credits'),
      description: t("Choose how you'd like to use your credits"),
      rightActions: undefined,
    },
    'media-balance': {
      title: t('Redeem for Media Balance'),
      description: t('Convert your credits to prepaid media balance'),
      rightActions: mediaBalanceActions,
    },
    success: {
      title: undefined,
      description: undefined,
      rightActions: undefined,
    },
  };

  const stepContent = stepMap[modalStep];

  return (
    <Modal
      allowOverflow={modalStep === 'media-balance'}
      description={stepContent.description}
      onClose={handleClose}
      opened={opened}
      rightActions={stepContent.rightActions}
      size={'default'}
      title={stepContent.title}
    >
      {modalStep === 'select' && (
        <RedemptionTypeSelect
          availableCredits={availableCredits}
          isLoading={isLoadingCredits}
          onSelect={handleTypeSelect}
        />
      )}
      {modalStep === 'media-balance' && (
        <MediaBalanceForm
          availableCredits={availableCredits}
          convertedAmount={convertedAmount}
          creditsToRedeem={formData.creditsToRedeem}
          isCalculating={isCalculating}
          isLoadingProject={isLoadingProject}
          onCreditsChange={handleCreditsChange}
          onProjectChange={handleProjectChange}
          projectBalance={projectBalance}
          projectName={formData.selectedProjectName}
          selectedProjectId={formData.selectedProjectId}
          validationErrors={validationErrors}
        />
      )}
      {modalStep === 'success' && (
        <RedemptionSuccess
          convertedAmount={convertedAmount}
          onDone={handleClose}
          projectName={formData.selectedProjectName}
        />
      )}
    </Modal>
  );
}

export default observer(RedeemCreditsModal);
